import PropTypes from 'prop-types';
import { useMemo } from 'react';

// material-ui
import { createTheme, StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';

// project imports
import useConfig from 'hooks/useConfig';
import { ToastProvider } from 'contexts/ToastContext';
import CustomShadows from './custom-shadows';
import componentsOverride from './overrides';
import { buildPalette } from './palette';
import Typography from './typography';

// ==============================|| DEFAULT THEME - MAIN ||============================== //

const globalStyles = (
  <GlobalStyles
    styles={{
      '@import': "url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap')",
      '*': {
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(31, 91, 64, 0.18) transparent',
      },
      '*::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
      },
      '*::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(31, 91, 64, 0.18)',
        borderRadius: '8px',
      },
      '*::-webkit-scrollbar-thumb:hover': {
        backgroundColor: 'rgba(31, 91, 64, 0.32)',
      },
      '*::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
      },
      'body': {
        fontFamily: "'Inter', 'Public Sans', sans-serif",
      },
      '@keyframes fadeInUp': {
        from: { opacity: 0, transform: 'translateY(12px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
      },
      '@keyframes fadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
      '@keyframes slideInLeft': {
        from: { opacity: 0, transform: 'translateX(-16px)' },
        to: { opacity: 1, transform: 'translateX(0)' },
      },
      '@keyframes scaleIn': {
        from: { opacity: 0, transform: 'scale(0.95)' },
        to: { opacity: 1, transform: 'scale(1)' },
      },
      '@keyframes shimmer': {
        '0%': { backgroundPosition: '-200% 0' },
        '100%': { backgroundPosition: '200% 0' },
      },
      '@keyframes heroPulse': {
        '0%, 100%': { boxShadow: '0 4px 12px rgba(15, 45, 32, 0.06), 0 12px 32px rgba(15, 45, 32, 0.06)' },
        '50%': { boxShadow: '0 4px 20px rgba(31, 91, 64, 0.14), 0 0 0 3px rgba(31, 91, 64, 0.08)' }
      },
      '@keyframes chartReveal': {
        from: { opacity: 0, transform: 'scale(0.92)' },
        to: { opacity: 1, transform: 'scale(1)' }
      },
      '@keyframes progressGrow': {
        from: { transform: 'scaleX(0)' },
        to: { transform: 'scaleX(1)' }
      },
    }}
  />
);

export default function ThemeCustomization({ children }) {
  const { state } = useConfig();

  const themeTypography = useMemo(() => Typography(state.fontFamily), [state.fontFamily]);

  const palette = useMemo(() => buildPalette(state.presetColor), [state.presetColor]);

  const themeOptions = useMemo(
    () => ({
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1440
        }
      },
      direction: 'ltr',
      mixins: {
        toolbar: {
          minHeight: 64,
          paddingTop: 8,
          paddingBottom: 8
        }
      },
      typography: themeTypography,
      colorSchemes: {
        light: {
          palette: palette.light,
          customShadows: CustomShadows(palette.light, 'light')
        }
      },
      shape: {
        borderRadius: 10
      },
      cssVariables: {
        cssVarPrefix: '',
        colorSchemeSelector: 'data-color-scheme'
      }
    }),
    [themeTypography, palette]
  );

  const themes = createTheme(themeOptions);
  themes.components = componentsOverride(themes);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider disableTransitionOnChange theme={themes} modeStorageKey="theme-mode" defaultMode='light'>
        <CssBaseline enableColorScheme />
        {globalStyles}
        <ToastProvider>{children}</ToastProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}

ThemeCustomization.propTypes = { children: PropTypes.node };
